import { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@/hooks/use-media-query'
import { t } from '@/lib/i18n'

// ConfirmDialog is a11y-correct by default: role=dialog with aria-modal, focus
// trapped inside while open, Escape and backdrop click both dismiss, and the
// previously-focused element is restored on close. Use this instead of
// window.confirm() or ad-hoc inline panels so the UX is consistent and
// screen-reader users get the modal semantics they expect.
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = t('confirm'),
  cancelLabel = t('cancel'),
  confirmKind = 'primary',
  requireText,
  busy,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmKind?: 'primary' | 'danger'
  // When set, the confirm button stays disabled until the user types this
  // exact string into an inline input — used for the riskiest actions.
  requireText?: string
  busy?: boolean
}) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null)
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const [typed, setTyped] = useState('')
  // On a very narrow screen the right-aligned button row overflows; stack it.
  const stackButtons = useMediaQuery('(max-width: 380px)')

  // Focus management + body scroll lock + escape handling.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Initial focus: the safest button (cancel), or the input if a typed
    // confirmation is required — moving the user into the text field mirrors
    // what window.prompt() would have done and avoids accidental confirms.
    const target = requireText ? inputRef.current : cancelBtnRef.current
    target?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      previouslyFocused.current?.focus()
      setTyped('')
    }
  }, [open, requireText, onClose])

  if (!open) return null

  const canConfirm = !busy && (!requireText || typed === requireText)
  const confirmBg = confirmKind === 'danger' ? 'var(--red)' : 'var(--accent)'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 80,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={panelRef}
        style={{
          maxWidth: '28rem', width: '100%', maxHeight: '90dvh',
          display: 'flex', flexDirection: 'column',
          backgroundColor: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
        }}
      >
        <div style={{ overflow: 'auto', minHeight: 0 }}>
          <h2
            id="confirm-dialog-title"
            style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--white)', marginBottom: '0.5rem' }}
          >
            {title}
          </h2>
          {description && (
            <div style={{ fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.5, marginBottom: '1rem' }}>
              {description}
            </div>
          )}
          {requireText && (
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.375rem' }}>
                {t('typeToConfirm', { x: requireText })}
              </p>
              <input
                ref={inputRef}
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={requireText}
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                  backgroundColor: 'var(--bg)',
                  border: `1px solid ${confirmKind === 'danger' ? 'var(--red)' : 'var(--border)'}`,
                  color: 'var(--text)', fontSize: '0.875rem', outline: 'none',
                }}
              />
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: stackButtons ? 'column-reverse' : 'row',
            justifyContent: 'flex-end', gap: '0.5rem',
            marginTop: '1rem', flexShrink: 0,
          }}
        >
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '0.5rem 0.875rem', borderRadius: '0.375rem',
              backgroundColor: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              fontSize: '0.875rem',
              minHeight: 44,
              width: stackButtons ? '100%' : undefined,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{
              padding: '0.5rem 0.875rem', borderRadius: '0.375rem',
              backgroundColor: confirmBg,
              color: confirmKind === 'danger' ? 'white' : 'var(--on-accent)',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              minHeight: 44,
              width: stackButtons ? '100%' : undefined,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.5,
            }}
          >
            {busy ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
